import "@/App.css";
import { BrowserRouter, Navigate, Route, Routes } from "react-router-dom";
import { Toaster } from "sonner";
import { DemoProvider } from "@/store/DemoStore";
import DocumentMeta from "@/components/DocumentMeta";
import { useAppViewport } from "@/hooks/useAppViewport";
import PortalLogin from "@/pages/PortalLogin";
import Launcher from "@/pages/Launcher";
import PublicQuote from "@/pages/PublicQuote";
import PublicInvite from "@/pages/PublicInvite";
import "@/lib/pwaInstall";
import InviteAccept from "@/pages/InviteAccept";
import SurveyPage from "@/pages/SurveyPage";
import GroupPublic from "@/pages/public/GroupPublic";
import { AgentsLayout, AgentsOperations, AgentsCommissions, AgentsPermissions, AgentsTab } from "@/pages/agent/Agents";
import { AdminOnly, AdminPlaceholder } from "@/pages/agent/AdminPages";
import UsersPage from "@/pages/agent/Users";
import { CommunicationLayout, CommunicationTab, AutomationTab, COMM_TABS } from "@/pages/agent/Communication";
import SurveysTab from "@/pages/agent/SurveysTab";
import FeedbackTab from "@/pages/agent/FeedbackTab";
import { RequireAuth } from "@/components/AuthGate";
import TopsailHome from "@/pages/home/TopsailHome";
import AgentLayout from "@/pages/agent/AgentLayout";
import AgentDashboard from "@/pages/agent/Dashboard";
import ClientsPage from "@/pages/agent/Clients";
import ClientProfile from "@/pages/agent/ClientProfile";
import TripsPage from "@/pages/agent/Trips";
import GroupsPage from "@/pages/agent/Groups";
import GroupWorkspace from "@/pages/agent/GroupWorkspace";
import TripWorkspace from "@/pages/agent/TripWorkspace";
import QuotesPage from "@/pages/agent/Quotes";
import QuoteDetail from "@/pages/agent/QuoteDetail";
import MessagesPage from "@/pages/agent/Messages";
import SettingsPage from "@/pages/agent/Settings";
import ClientLayout from "@/pages/client/ClientLayout";
import ClientHome from "@/pages/client/Home";
import ClientTrips from "@/pages/client/MyTrips";
import ClientTripDetail from "@/pages/client/TripDetail";
import ClientMessages from "@/pages/client/MyMessages";
import ClientProfilePage from "@/pages/client/MyProfile";

function AppRouter() {
  return (
    <>
        <DocumentMeta />
        <Routes>
          <Route path="/" element={<TopsailHome />} />
          <Route path="/portal" element={<PortalLogin />} />
          <Route path="/launch" element={<Launcher />} />
          <Route path="/quote/:token" element={<PublicQuote />} />
          <Route path="/t/:token" element={<PublicInvite />} />
          <Route path="/invite/:token" element={<InviteAccept />} />
          <Route path="/survey/:token" element={<SurveyPage />} />
          <Route path="/g/:code" element={<GroupPublic />} />
          <Route path="/home" element={<Navigate to="/" replace />} />
          <Route path="/agent" element={<RequireAuth kind="agent"><AgentLayout /></RequireAuth>}>
            <Route index element={<AgentDashboard />} />
            <Route path="agents" element={<AdminOnly><AgentsLayout /></AdminOnly>}>
              <Route index element={<Navigate to="operations" replace />} />
              <Route path="operations" element={<AgentsOperations />} />
              <Route path="commissions" element={<AgentsCommissions />} />
              <Route path="permissions" element={<AgentsPermissions />} />
              <Route path="talent" element={<AgentsTab testId="agents-talent-page" title="Talent" />} />
            </Route>
            <Route path="users" element={<AdminOnly><UsersPage /></AdminOnly>} />
            <Route path="communication" element={<AdminOnly><CommunicationLayout /></AdminOnly>}>
              <Route index element={<Navigate to="automation" replace />} />
              <Route path="automation" element={<AutomationTab />} />
              <Route path="surveys" element={<SurveysTab />} />
              <Route path="feedback" element={<FeedbackTab />} />
              {COMM_TABS.filter(([to]) => to !== "automation" && to !== "surveys" && to !== "feedback").map(([to, label]) => (
                <Route key={to} path={to} element={<CommunicationTab testId={`communication-${to}-page`} title={label} />} />
              ))}
            </Route>
            <Route path="marketing" element={<AdminOnly><AdminPlaceholder testId="admin-marketing-page" title="Marketing" subtitle="Admin only · campaigns and promotions." /></AdminOnly>} />
            <Route path="clients" element={<ClientsPage />} />
            <Route path="clients/:id" element={<ClientProfile />} />
            <Route path="trips" element={<TripsPage />} />
            <Route path="trips/:id" element={<TripWorkspace />} />
            <Route path="groups" element={<GroupsPage />} />
            <Route path="groups/:id" element={<GroupWorkspace />} />
            <Route path="quotes" element={<QuotesPage />} />
            <Route path="quotes/:id" element={<QuoteDetail />} />
            <Route path="messages" element={<MessagesPage />} />
            <Route path="messages/:id" element={<MessagesPage />} />
            <Route path="settings" element={<SettingsPage />} />
          </Route>
          <Route path="/mytrip" element={<RequireAuth kind="client"><ClientLayout /></RequireAuth>}>
            <Route index element={<ClientHome />} />
            <Route path="trips" element={<ClientTrips />} />
            <Route path="trips/:id" element={<ClientTripDetail />} />
            <Route path="messages" element={<ClientMessages />} />
            <Route path="profile" element={<ClientProfilePage />} />
          </Route>
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
    </>
  );
}

function App() {
  useAppViewport();
  return (
    <DemoProvider>
      <BrowserRouter>
        <AppRouter />
      </BrowserRouter>
      <Toaster position="top-center" toastOptions={{ className: "font-sans" }} />
    </DemoProvider>
  );
}

export default App;
